import styled, {useColors} from '@pinecast/styles';
import type * as React from 'react';

import {screenReaderOnlyStyles} from './ScreenReaderOnly';

const Text = styled('span');

export type StatusMessageProps = {
  /** Shown, and announced whenever it changes. */
  message: React.ReactNode;
  /**
   * Announced, but not shown, while there is no `message`: a busy control
   * shows that with a spinner, which a screen reader can't see.
   */
  pendingMessage?: React.ReactNode;
  /** `negative` messages are errors, and are announced right away. */
  type?: 'positive' | 'negative';
  /**
   * Keeps the message off screen, for a caller that shows it another way,
   * such as `StatusToast`. It is still announced.
   */
  visuallyHidden?: boolean;
};

/**
 * A short status message: "Saved", "Copied", or why something failed.
 *
 * The message is written into live regions that are always rendered, since a
 * screen reader only announces a region that exists before its text changes:
 * `status` for progress and success, `alert` for errors. Only one of them holds
 * text at a time, so each message is announced once. With nothing to show,
 * they take no space.
 *
 * Render it where the message belongs on screen. `ButtonLabel` puts it beside a
 * button, and `StatusToast` shows it in a floating toast instead.
 */
const StatusMessage = ({
  message,
  pendingMessage,
  type,
  visuallyHidden = false,
}: StatusMessageProps) => {
  const designColors = useColors();
  const hasMessage = message != null && message !== false && message !== '';
  const isError = hasMessage && type === 'negative';
  return (
    <Text
      style={
        hasMessage && !visuallyHidden
          ? {
              color:
                type === 'positive'
                  ? designColors.badgeGreenText
                  : type === 'negative'
                    ? designColors.badgeRedText
                    : designColors.badgeGrayText,
              fontSize: 14,
            }
          : screenReaderOnlyStyles
      }
    >
      <span role="status">
        {hasMessage ? (isError ? null : message) : pendingMessage}
      </span>
      <span role="alert">{isError ? message : null}</span>
    </Text>
  );
};

export default StatusMessage;
